import React from "react";
import { Box, Typography } from "@mui/material";
import { slimScrollbarSx } from "../../../../../utils/scrollbarStyles";

// Static label sitting ABOVE the field, instead of MUI's default floating
// label that starts life sitting inside the outlined border (Rahim,
// 2026-09-02: "get the placeholder text outside the text box right now its
// inside the box"). Every TextField/Select on this page is wrapped with
// this instead of passing `label` straight to the input. Extracted to its
// own file (not a const inside AgentConfigView.jsx) so sub-components like
// ResponseFormatSection.jsx/OfficeHoursSection.jsx can import it without a
// circular import back through AgentConfigView.jsx, which imports them —
// same reasoning as PillSwitch.jsx.
// `labelAction` — optional element (e.g. an expand IconButton) rendered at
// the right end of the label row. Added for the Context field's fullscreen-
// editor trigger (Rahim, 2026-09-04) — absent by default so every other
// existing caller renders exactly as before.
export function LabeledField({ label, sx, labelAction, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        {/* text.secondary/500 — confirmed this is what both Reports (filter
            pills) and Inbox actually use for this tier of label/caption text. */}
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.secondary" }}>
          {label}
        </Typography>
        {labelAction}
      </Box>
      {children}
    </Box>
  );
}

// Same outlined-input treatment Inbox uses on its own TextFields (e.g.
// InboxAll.jsx's contact Name/Phone dialog fields) — 8px radius, divider
// border that darkens to text.secondary on hover, 0.875rem text.
export const inboxInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    // theme.palette.divider is only 12% opacity in dark mode — nearly
    // invisible against the dark panel background. Bumped to MUI's own
    // default outlined-input border opacity (23%) for dark mode only;
    // light mode's divider already reads fine, left unchanged.
    "& fieldset": {
      borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : theme.palette.divider),
    },
    "&:hover fieldset": { borderColor: (theme) => theme.palette.text.secondary },
  },
  // Fixes native <input type="time"> (Office Hours' schedule rows)
  // rendering its digits/AM-PM text AND its calendar-picker clock icon in
  // solid black in dark mode (Rahim, 2026-09-03, screenshot: "still uses
  // black colour for text"), even though the input's own CSS `color` was
  // already correctly theme-light-grey. Root cause: those glyphs are
  // browser-native UI chrome, not real text — Chromium draws them based
  // on the `color-scheme` CSS property, completely separate from `color`,
  // and it was left at its default "normal" (i.e. light) regardless of
  // the app's own theme. Setting color-scheme on the actual <input> node
  // (not just an ancestor) is what's required for Chromium to redraw
  // those native parts for dark backgrounds. Harmless on every other
  // TextField type here too — normal text inputs/textareas don't have
  // this kind of native-drawn chrome to be affected by it.
  "& .MuiInputBase-input": {
    colorScheme: (theme) => (theme.palette.mode === "dark" ? "dark" : "light"),
  },
};

// Same treatment as inboxInputSx but for a Select (no `.MuiOutlinedInput-root`
// wrapper the way TextField has — Select's own root IS the outlined input).
// Rahim, 2026-09-03: "the drop downs ... the text for the whole container is
// not same as above cards" — these Selects had zero custom styling at all
// (plain MUI default 4px radius, floating InputLabel), the one inconsistent
// spot on an otherwise fully-restyled page.
export const inboxSelectSx = {
  borderRadius: "8px",
  fontSize: "0.875rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : theme.palette.divider),
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: (theme) => theme.palette.text.secondary },
};

// Popover paper + row treatment lifted directly from ChatInfo.jsx's own
// "Add label" Select (same convention the Knowledge Sources picker dialog
// on this page already borrowed) — rounded popover, rounded-pill/inset
// hover rows, no MUI default harsh green Mui-selected background. Rahim,
// 2026-09-03: "the icon and the drop down should have good size too right
// now very weird drop down size" (re: Message Format's 3 Selects, whose
// open menu was still on MUI's stock green-tinted checkbox rows).
// Matches ChatInfo.jsx's own "Add label" Select MenuProps exactly (Rahim,
// 2026-09-03: "use the type of drop down we made in inbox") — no
// anchorOrigin/transformOrigin override, no maxHeight cap, just the rounded
// PaperProps. Chasing the overlap bug with those two knobs (a forced
// anchor, then a tight maxHeight) turned out to be solving the wrong
// problem: Message Format used to be the LAST section on the page, so its
// Selects had almost no room below to open into and tripped MUI Popover's
// built-in collision guard (which slides the paper up over the input when
// it doesn't fit — no anchor setting can prevent that, confirmed live).
// Moving that section higher up the page ("take it two steps up") gave it
// 400px+ of real room below instead, which is what actually fixes it — at
// which point Inbox's own plain, uncapped version behaves identically here
// too, so there's no reason for this page to carry the extra complexity.
export const inboxSelectMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "10px",
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0px 4px 12px rgba(0,0,0,0.4)" : "0px 4px 12px rgba(0,0,0,0.15)",
      border: (theme) => `1px solid ${theme.palette.divider}`,
      // Timezone's list (20+ entries) has no natural cap the way Message
      // Format's 3-4-item Selects do — with no maxHeight it just kept
      // growing past the bottom of the viewport (Rahim, 2026-09-03: "fix
      // sizing of this drop down banner ... its height is tooo long"),
      // scrolling via the browser's own default grey scrollbar instead of
      // this app's own. 320px comfortably shows ~8 rows before it needs
      // to scroll — every other Select on this page has far fewer options
      // than that and never hits the cap, so this only changes Timezone's
      // behavior in practice. slimScrollbarSx is the same thin-scrollbar
      // formula every Inbox scrollable panel already uses (Rahim: "use
      // the scrollbar as used in the inbox page").
      maxHeight: 320,
      ...slimScrollbarSx,
    },
  },
};
export const inboxMenuItemSx = (theme) => ({
  // Matches ChatInfo.jsx's own menuItemSx fontSize exactly (Rahim,
  // 2026-09-03: "the text and the clicked effect is still small i want it
  // to be like the inbox one") — briefly shrunk to 0.8125rem/py:0.625 to
  // fit the tight maxHeight cap the dropdown positioning bug needed at
  // the time; now that the reorder above gives the menu real room and
  // the cap is gone (inboxSelectMenuProps no longer sets maxHeight), that
  // shrink has no reason to stay and was just making rows — and the
  // hover/selected highlight riding on that same padding — read smaller
  // and weaker than Inbox's own.
  fontSize: "0.9375rem",
  borderRadius: "8px",
  mx: 0.5,
  // Explicit, controlled padding instead of MUI's own default MenuItem
  // padding (Rahim, 2026-09-03: "fix this weird big drop down") — same
  // compact scale the Knowledge Sources picker dialog's own rows use.
  py: 1,
  // ⚠️ The REAL cause of every earlier attempt still looking "massive":
  // confirmed via live computed heights that every row rendered at the
  // exact same 51px regardless of content (a 2-line row and three
  // single-line rows all identical) — that's MUI's own MenuItem
  // min-height floor (~48px), completely unrelated to padding, the
  // checkbox's size, or ListItemText's margin (all already fixed and
  // still didn't move this number). Overriding it directly is the actual
  // fix — single-line rows now shrink to their real content height.
  minHeight: "auto",
  "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)" },
  "&.Mui-selected": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" },
  "&.Mui-selected:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)" },
  // The last-interacted row in a multi-select kept a persistent green
  // highlight even with the mouse moved away — MUI tracks that separately
  // as keyboard/programmatic focus (.Mui-focusVisible), a state the rules
  // above don't touch. Neutral treatment for that too.
  "&.Mui-focusVisible": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" },
  "&.Mui-selected.Mui-focusVisible": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)" },
});

// A short, single-column field width instead of stretching the full page —
// the common convention for a name/description-style form input (Rahim,
// 2026-09-02: "use worldwide used width for the name and description").
// 480px is the same rough measure Stripe/Notion-style settings forms use
// for a single text field.
export const SHORT_FIELD_WIDTH = 480;

// MUI's default Skeleton fill is alpha(text.primary, 0.11) — reads as
// near-white on any surface that itself uses background.paper (white in
// light mode): the Knowledge Sources table card, the knowledge-source
// picker Dialog, the Assign-to Popover (Rahim, 2026-09-04: "change the
// colour of skeleton...to grey not white", found while auditing every
// Skeleton on this page against its real container's background — the two
// living inside SectionCard were already fine there, since SectionCard is
// transparent in light mode, not white). Explicit grey, still theme-aware
// (dark mode's default already read fine, kept equivalent here).
export const skeletonSx = {
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.13)" : "#e0e0e0"),
};
